import { tokenStorage } from "./tokenStorage";

// AuthHeader is a class that manages the authentication header for the application.
// For mobile: uses AsyncStorage for access token persistence
// For web: uses in-memory storage with localStorage fallback
class AuthHeaderManager {
  private static instance: AuthHeaderManager | null = null;
  private token: string | null = null;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): AuthHeaderManager {
    if (!AuthHeaderManager.instance) {
      AuthHeaderManager.instance = new AuthHeaderManager();
    }
    return AuthHeaderManager.instance;
  }

  /**
   * Initialize by loading token from storage (mobile only)
   * For web, tokens are managed in-memory
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      const storedToken = await tokenStorage.getAccessToken();
      if (storedToken) {
        this.token = storedToken;
      }
      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize AuthHeaderManager:", error);
      this.isInitialized = true; // Continue without persisted token
    }
  }

  public async setToken(token: string): Promise<void> {
    if (token.trim() === "") {
      throw new Error("Token cannot be empty");
    }

    // add Bearer prefix if it is not present
    const normalizedToken = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

    this.token = normalizedToken;

    // Persist token to storage
    try {
      await tokenStorage.setAccessToken(normalizedToken);
    } catch (error) {
      console.error("Failed to persist access token:", error);
      // Continue even if persistence fails
    }
  }

  // get Authorization header - return null if token is not set
  public getAuthorizationHeader(): { Authorization: string } | null {
    if (!this.token || this.token.trim().length === 0) {
      return null;
    }

    return { Authorization: this.token };
  }

  public getAuthorizationHeaderOrThrow(): AuthHeader {
    const header = this.getAuthorizationHeader();
    if (!header) {
      throw new Error("Token is not set");
    }
    return header;
  }

  public async clearToken(): Promise<void> {
    this.token = null;

    // Clear token from storage
    try {
      await tokenStorage.removeAccessToken();
    } catch (error) {
      console.error("Failed to clear access token from storage:", error);
    }
  }
}

export type AuthHeader = { Authorization: string };
export const authHeader: {
  initialize: () => Promise<void>;
  get: () => AuthHeader | null;
  getOrThrow: () => AuthHeader;
  set: (token: string) => Promise<void>;
  clear: () => Promise<void>;
} = {
  initialize: async () => await AuthHeaderManager.getInstance().initialize(),
  get: () => AuthHeaderManager.getInstance().getAuthorizationHeader(),
  getOrThrow: () => AuthHeaderManager.getInstance().getAuthorizationHeaderOrThrow(),
  set: async (token: string) => {
    await AuthHeaderManager.getInstance().setToken(token);
  },
  clear: async () => {
    await AuthHeaderManager.getInstance().clearToken();
  },
};
